
import React, { Suspense } from 'react';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { SidebarProvider } from '@/contexts/SidebarContext';
import { AppSidebar } from '@/components/layout/AppSidebar';
import { SidebarInset, SidebarTrigger } from '@/components/ui/sidebar';
import { PageStateProvider } from '@/contexts/PageStateProvider';
import ScrollToTop from '@/components/layout/ScrollToTop';

// This is the main app layout with sidebar - ONLY used for protected routes
const ProtectedAppLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <PageStateProvider>
      <SidebarProvider>
        <ScrollToTop />
        <div className="min-h-screen flex w-full">
          <AppSidebar />
          <SidebarInset className="flex-1">
            <header className="flex h-16 shrink-0 items-center gap-2 border-b px-4">
              <SidebarTrigger className="-ml-1" />
            </header>
            <main className="flex-1">
              <Suspense fallback={
                <div className="flex items-center justify-center min-h-[50vh]">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                </div>
              }>
                {children}
              </Suspense>
            </main>
          </SidebarInset>
        </div>
      </SidebarProvider>
    </PageStateProvider>
  );
};

// Protected layout wrapper - wraps with auth and layout
export const ProtectedLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <ProtectedRoute>
      <ProtectedAppLayout>
        {children}
      </ProtectedAppLayout>
    </ProtectedRoute>
  );
};

// Open layout - same app shell as ProtectedLayout but without auth gating.
// Used for freemium features that are publicly accessible (signed-out OK).
export const OpenLayout = ({ children }: { children: React.ReactNode }) => {
  return (
    <ProtectedAppLayout>
      {children}
    </ProtectedAppLayout>
  );
};

